<template>
    <v-row justify="center" align="center">
        <v-col cols="12" sm="12" md="12">
            <import-page-banner
                title="Direct Order"
                sample-csv-path="/createDirectOrderByImportSample.csv"
            />
            
            <v-card>
                <v-card-title>
                    <v-row>
                        <v-col cols="12" sm="12" md="6"> Create Direct Order by Import </v-col>
                        <v-col cols="12" sm="12" md="6">
                            <common-list-reference
                                :btn-color="courierReferenceProps.btnColor"
                                :btn-text="courierReferenceProps.btnText"
                                :title="courierReferenceProps.title"
                                :description="courierReferenceProps.description"
                                :item-code-header="courierReferenceProps.itemCodeHeader"
                                :item-desc-header="courierReferenceProps.itemDescHeader"
                                :reference-list="courierReferenceProps.referenceList"
                            ></common-list-reference>
                        </v-col>
                    </v-row>
                </v-card-title>
                <v-card-text>
                    <p><b>Only accept csv file.</b></p>
                    <p>1. Choose your file</p>
                    <p>2. SELECT THE CORRECT MAPPING OF Field and Column</p>
                    <p>3. IMPORT</p>
                    <!-- New CSV Importer Component -->
                    <csv-importer
                        :field-definitions="data.fields"
                        :preview-headers="data.previewHeaders"
                        @data-loaded="onCsvDataLoaded"
                        @data-cleared="onCsvDataCleared"
                    />
                </v-card-text>
            </v-card>
            <v-alert v-if="importError" type="error" class="mt-3" closable @click:close="importError = ''">
                {{ importError }}
            </v-alert>
            <br />
            <v-card v-if="data.mappedCsv && data.mappedCsv.length > 0">
                <v-card-title>
                    <v-row>
                        <v-col cols="12" sm="12" md="12">
                            <v-btn
                                class="mr-2"
                                color="primary"
                                :loading="loading"
                                :disabled="loading || invalidFile"
                                @click="onImportClick"
                            >
                                <v-icon>mdi-cloud-upload</v-icon>
                                Import
                            </v-btn>
                            <v-btn class="mr-2" :to="pathListDirectOrder"> Back to list </v-btn>
                            <span v-if="importDone">{{ importDoneMsg }} </span>
                        </v-col>
                    </v-row>
                </v-card-title>
                <v-card-text>
                    <v-table>
                        <thead>
                            <tr>
                                <th class="text-left" v-for="header in data.headers" :key="header.value">
                                    {{ header.text }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(item, idx) in transformMappedCsv(data.mappedCsv)"
                                :key="`row${idx}`"
                                :class="invalidRowClass(item)"
                            >
                                <td v-for="header in data.headers" :key="header.value">
                                    {{ item[header.value] }}
                                </td>
                            </tr>
                        </tbody>
                    </v-table>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { defineComponent, reactive } from 'vue';
import { fetchWrapper } from '../../utils/FetchWrapper';
import { DirectOrderLabelTextHelper } from '../../utils/labelTextHelper/DirectOrderLabelTextHelper';
import * as _ from 'lodash';
import { PathDefinition } from '../../utils/PathDefinition';
import { ImportTableLabelTextHelper } from '../../utils/labelTextHelper/ImportTableLabelTextHelper';
import { SharedMsgHelper } from '../../utils/SharedMsgHelper';
import CommonListReference from '../../components/CommonListReference.vue';
import { Formatter } from '../../utils/Formatter';
import { MasterDataApiCaller } from '../../utils/MasterDataApiCaller';
import { DirectOrderHeaderHelper } from '../../utils/DirectOrderHeaderHelper';
import { ImportHeaderHelper } from '../../utils/ImportHeaderHelper';
import CsvImporter from '../../components/CsvImporter.vue';
import ImportPageBanner from '../../components/ImportPageBanner.vue';
import { ImportValidationHelper } from '../../utils/ImportValidationHelper';
import { DirectOrderImportValidation } from '../../config/importValidationConfigs';
import { DirectOrderImportRetry } from '../../utils/DirectOrderImportRetry';

export default defineComponent({
    name: 'ImportDirectOrder',

    components: {
        CommonListReference,
        CsvImporter,
        ImportPageBanner
    },

    setup() {
        const data = reactive({
            mappedCsv: null,
            fields: DirectOrderHeaderHelper.getImportDirectOrderFields(),
            previewHeaders: DirectOrderHeaderHelper.getImportDirectOrderHeaders(),
            headers: [
                ...ImportHeaderHelper.getHeaders(),
                DirectOrderHeaderHelper.getDirectOrderNumberHeaderItem(),
                DirectOrderHeaderHelper.getTrackingNumberHeaderItem(),
                ...DirectOrderHeaderHelper.getImportDirectOrderHeaders()
            ]
        });
        return {
            data
        };
    },
    data() {
        return {
            loading: false,
            invalidFile: false,
            importDone: false,
            importJobId: '',
            importError: '',
            importDoneMsg: SharedMsgHelper.getImportDoneMsg,
            pathListDirectOrder: PathDefinition.listDirectOrder,
            formLabelText: DirectOrderLabelTextHelper.getLabelText,
            //todo: change to country list
            courierCodeList: [],
            courierReferenceProps: {
                btnColor: 'secondary',
                btnText: 'Service List',
                title: 'Service List For Reference',
                description: '',
                itemCodeHeader: '',
                itemDescHeader: 'Service',
                referenceList: []
            }
        };
    },
    async created() {
        this.courierCodeList = await MasterDataApiCaller.getMasterCourierList();
        this.courierReferenceProps.referenceList = _.map(this.courierCodeList, (code) => {
            return {
                code: '',
                desc: code
            };
        });
    },
    methods: {
        onCsvDataLoaded(mappedData) {
            // Receive data from CSV importer component
            this.data.mappedCsv = mappedData;
            this.importError = '';
        },
        onCsvDataCleared() {
            // Clear data when file is removed
            this.data.mappedCsv = null;
            this.importDone = false;
            this.invalidFile = false;
        },
        transformMappedCsv(mappedCsv) {
            const importHeaderValues = this.data.headers.map((header) => header.value);
            const lowercaseFields = ['directOrderCountry'];
            
            // First apply validation
            let validatedRows = ImportValidationHelper.validateRows(mappedCsv, DirectOrderImportValidation, importHeaderValues);
            
            // Then apply lowercase formatting
            return _.map(validatedRows, (item) => {
                lowercaseFields.forEach((field) => {
                    if (item[field] && item[field] !== SharedMsgHelper.getMissingRequiredErrMsg) {
                        item[field] = Formatter.lowerCaseFormatter(item[field]);
                    }
                });
                return item;
            });
        },
        async onImportClick() {
            this.data.mappedCsv = this.transformMappedCsv(this.data.mappedCsv);
            if (this.data.mappedCsv && this.data.mappedCsv.length > 0) {
                await this.importSubmit();
            } else {
                console.log('empty csv');
            }
        },
        async importSubmit() {
            this.loading = true;
            try {
                const isValid = this.validation(this.data.mappedCsv);
                if (isValid) {
                    const requestFields = DirectOrderHeaderHelper.getImportDirectOrderHeaders().map((header) => header.value);
                    const retry = await DirectOrderImportRetry.prepare(this.data.mappedCsv, requestFields);
                    this.importJobId = retry.jobId;
                    retry.succeeded.forEach((succeeded, index) => {
                        this.data.mappedCsv[index]._importSucceeded = succeeded;
                        this.data.mappedCsv[index]._idempotencyKey = retry.keys[index];
                        if (succeeded) this.data.mappedCsv[index].status = ImportTableLabelTextHelper.getImportResultValue.success;
                    });
                    for (const item in this.data.mappedCsv) {
                        if (!retry.succeeded[item]) await this.create(item);
                    }
                    const allSucceeded = this.data.mappedCsv.every((row) => row._importSucceeded);
                    this.importDone = allSucceeded;
                    this.invalidFile = allSucceeded;
                } else {
                    console.log('invalid csv');
                }
            } catch (error) {
                this.importError = error instanceof Error ? error.message : 'Order import could not be prepared';
                this.importDone = false;
            } finally {
                this.loading = false;
            }
        },
        validation(list) {
            return _.every(list, (v) => {
                return !this.isInvalidRowContent(v);
            });
        },
        invalidRowClass(item) {
            if (this.isInvalidRow(item)) {
                return 'row-validation-error';
            }
            return '';
        },
        isInvalidRow(item) {
            return (
                this.isInvalidRowContent(item) || item.status === ImportTableLabelTextHelper.getImportResultValue.error
            );
        },
        isInvalidRowContent(item) {
            return ImportValidationHelper.hasValidationErrors(item, DirectOrderImportValidation);
        },
        async create(idx) {
            const csvData = this.data.mappedCsv[idx];
            const response = await this.createDirectOrder(csvData);
            await this.processResponse(response, idx, csvData);
        },
        async createDirectOrder(csvData) {
            const requestFields = DirectOrderHeaderHelper.getImportDirectOrderHeaders().map((header) => header.value);
            const request = _.pick(csvData, requestFields);
            const requestUrl = `/api/customer/directOrder/create`;
            return await fetchWrapper.post(requestUrl, request, { 'Idempotency-Key': csvData._idempotencyKey });
        },
        async processResponse(response, idx, csvData) {
            if (response.success) {
                csvData.status = ImportTableLabelTextHelper.getImportResultValue.success;
                csvData.directOrderNumber = _.get(response, 'result.directOrderNumber');
                csvData.trackingNumber = _.get(response, 'result.trackingNumber');
                csvData._importSucceeded = true;
                await DirectOrderImportRetry.markSucceeded(this.importJobId, Number(idx));
            } else {
                csvData.status = ImportTableLabelTextHelper.getImportResultValue.error;
                csvData.error = response.errors;
            }
            this.data.mappedCsv.splice(idx, 1, csvData);
        }
    }
});
</script>

<style>
select {
    border: 1px solid !important;
}

.row-validation-error {
    background: red;
}
</style>
